import { Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import styled from '@emotion/styled';

import { BannerGroupBannerList, ShopUxBannerLinkType, useDayLeftTimer } from '@domains/storehome-common';

interface Props {
  banner_item: BannerGroupBannerList;
  is_swiper?: boolean;
  onClick?: ({
    link,
    link_type,
    id,
  }: {
    link: string | undefined;
    link_type: ShopUxBannerLinkType | null;
    id: string;
  }) => void;
}

export const BannerItem = ({ banner_item, is_swiper = true, onClick }: Props) => {
  const { id, image_url, title, link_type, link_button, date_ended } = banner_item;
  const { formatted_time } = useDayLeftTimer({ end_timestamp: date_ended, timer_threshold_days: 1 });

  if (!image_url) {
    return null;
  }

  return (
    <RatioWrap>
      <Item onClick={() => onClick?.({ link: link_button?.link_url, link_type, id })}>
        {image_url && <Image src={image_url} alt={title ?? ''} />}
        <Dim />
        {date_ended && (
          <Timer variant='Caption_11_Medium' as='p' color='staticWhite' is_swiper={is_swiper}>
            {formatted_time}
          </Timer>
        )}
        <Title variant='Body_18_Multiline_Bold' is_swiper={is_swiper}>
          {title}
        </Title>
      </Item>
    </RatioWrap>
  );
};

const RatioWrap = styled.div`
  position: relative;
  padding: 0 0 60% 0;
  overflow: hidden;
  width: 100%;
  height: 0;
`;

const Item = styled.div`
  display: inline-block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
`;

const Dim = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 49.76%, rgba(0, 0, 0, 0.3) 100%);
  pointer-events: none;
`;

const Timer = styled(Text)<{ is_swiper: boolean }>`
  padding: 4px 8px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 8px;
  left: 8px;
  background: ${vars.color.blackOpacity30};
  border-radius: 4px;
  opacity: ${({ is_swiper }) => (is_swiper ? 0 : 1)};
  transition: opacity 200ms ease-in-out 250ms;
  .swiper-slide-active & {
    opacity: 1;
  }
`;

const Title = styled(Text)<{ is_swiper: boolean }>`
  position: absolute;
  padding: 0 8px;
  width: 100%;
  max-height: 52px;
  left: 50%;
  bottom: 32px;
  font-size: 18px;
  line-height: 25.2px;
  color: ${vars.color.staticWhite};
  overflow: hidden;
  white-space: pre;
  text-align: center;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  opacity: ${({ is_swiper }) => (is_swiper ? 0 : 1)};
  transform: translateX(-50%) translateY(${({ is_swiper }) => (is_swiper ? '5px' : '0px')});
  transition:
    opacity 400ms ease-in-out 250ms,
    transform 200ms ease-in-out 250ms;
  .swiper-slide-active & {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;
